
import { useState, useMemo, useEffect } from 'react';
import { AppstoreOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Spin, Alert, Card, Typography, Button, Tag, Row, Col } from 'antd';
import { Brain, GraduationCap, Presentation, Shield, Database, Cube } from 'iconoir-react';
import { fetchAppStoreApps, getDefaultApps } from './AppStore/lib/api';
import type { AppFromApi } from './AppStore/lib/api';
import { transformAppForDisplay } from './AppStore/config/apps';
import type { DisplayApp } from './AppStore/config/apps';
import { useNavigate } from 'react-router-dom';
import { useAppPermissions } from '../hooks/useAppPermissions';
import type { AppKey } from '../hooks/useAppPermissions';

const { Title, Text, Paragraph } = Typography;

// Icon mapping for each app using iconoir
const iconMap: Record<string, React.ReactElement> = {
  'thiranai': <Brain width={32} height={32} strokeWidth={2} />,
  'training': <GraduationCap width={32} height={32} strokeWidth={2} />,
  'demo': <Presentation width={32} height={32} strokeWidth={2} />,
  'iam': <Shield width={32} height={32} strokeWidth={2} />,
  'finetuning': <Database width={32} height={32} strokeWidth={2} />,
  'agent-builder': <Cube width={32} height={32} strokeWidth={2} />,
};

interface AppTileProps {
  app: DisplayApp;
  onClick: () => void;
}

const AppTile = ({ app, onClick }: AppTileProps) => {
  const iconKey = app.key || 'thiranai';
  const AppIcon = iconMap[iconKey] || iconMap['thiranai'];
  return (
    <Card
      hoverable
      onClick={onClick}
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        height: '100%',
        transition: 'all 0.3s ease',
        border: '1px solid #e5e7eb',
      }}
      bodyStyle={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${app.gradient[0]}, ${app.gradient[1]})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          boxShadow: `0 4px 16px ${app.gradient[0]}30`,
          color: '#ffffff',
        }}
      >
        {AppIcon}
      </div>
      <Title level={5} style={{ marginBottom: '4px', marginTop: 0 }}>
        {app.name}
      </Title>
      {app.badge && (
        <Tag color={app.badge === 'new' ? 'green' : 'blue'} style={{ marginBottom: '8px' }}>
          {app.badge.toUpperCase()}
        </Tag>
      )}
      <Paragraph
        type="secondary"
        style={{ fontSize: '13px', marginBottom: 0, flex: 1 }}
        ellipsis={{ rows: 2 }}
      >
        {app.description}
      </Paragraph>
    </Card>
  );
};

export default function AppStorePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [apps, setApps] = useState<DisplayApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetchAppStoreApps(token || undefined);

      const displayApps = response.apps
        .filter((app: AppFromApi) => app.isEnabled && app.isVisible)
        .map(transformAppForDisplay);

      setApps(displayApps);
    } catch (err) {
      console.error('Failed to load apps:', err);
      setError('Failed to load apps from server. Using defaults.');

      const defaults = getDefaultApps();
      const displayApps = defaults.apps
        .filter((app) => app.isEnabled && app.isVisible)
        .map(transformAppForDisplay);

      setApps(displayApps);
    } finally {
      setLoading(false);
    }
  };

  const { hasPermission, isAdmin } = useAppPermissions();

  const filteredApps = useMemo(() => {
    let filtered = apps;

    // Filter by permissions if not admin
    if (!isAdmin) {
      filtered = filtered.filter(app => {
        // Map app ID/key to permission key
        // app.key should match AppKey type (admin, iam, training, etc.)
        return hasPermission(app.id.replace('default_', '') as AppKey);
      });
    }

    if (!searchQuery) return filtered;

    const query = searchQuery.toLowerCase();
    return filtered.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        app.description.toLowerCase().includes(query) ||
        app.category.toLowerCase().includes(query)
    );
  }, [apps, searchQuery, hasPermission, isAdmin]);

  const handleAppClick = (app: DisplayApp) => {
    if (app.url.startsWith('http')) {
      window.open(app.url, '_blank');
    } else if (app.url.startsWith('#')) {
      // Internal hash route - navigate within app
      const path = app.url.replace('#', '');
      navigate(`/app${path}`);
    } else {
      navigate(app.url);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          {getGreeting()} 👋
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Welcome to ThiranTech - Your AI-powered workspace
        </Text>
      </div>

      {/* Error Banner */}
      {error && (
        <Alert
          message={error}
          type="warning"
          showIcon
          style={{ marginBottom: '24px', maxWidth: '600px', margin: '0 auto 24px' }}
        />
      )}

      {/* Search and Refresh */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '32px',
          maxWidth: '600px',
          margin: '0 auto 32px',
        }}
      >
        <Input
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder="Search applications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="large"
          style={{ flex: 1 }}
        />
        <Button
          icon={<ReloadOutlined spin={loading} />}
          size="large"
          onClick={loadApps}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Loading State */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: '#6b7280' }}>Loading applications...</div>
        </div>
      ) : (
        <>
          {/* Apps Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              maxWidth: '1200px',
              margin: '0 auto 24px',
            }}
          >
            <AppstoreOutlined style={{ fontSize: '24px', color: '#6b7280' }} />
            <Title level={4} style={{ margin: 0 }}>
              {searchQuery ? 'Search Results' : 'Applications'}
            </Title>
            <Tag>{filteredApps.length}</Tag>
          </div>

          {/* App Grid */}
          {filteredApps.length > 0 ? (
            <Row gutter={[16, 16]} style={{ maxWidth: '1200px', margin: '0 auto' }}>
              {filteredApps.map((app) => (
                <Col xs={24} sm={12} md={8} lg={6} key={app.id}>
                  <AppTile app={app} onClick={() => handleAppClick(app)} />
                </Col>
              ))}
            </Row>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <Title level={4}>No applications found</Title>
              <Text type="secondary">Try adjusting your search</Text>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div
        style={{
          textAlign: 'center',
          padding: '48px 0 24px',
          color: '#9ca3af',
          fontSize: '14px',
        }}
      >
        © 2025 ThiranTech. Powered by AI. Built with ❤️
      </div>
    </div>
  );
}
